import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowDown, ArrowUpRight } from "lucide-react";

const LINES = ["MOVE.", "RECOVER.", "THRIVE."];

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.18, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden bg-navy">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <div className="absolute inset-0 bg-navy" />
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-electric/15 blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-electric/10 blur-[120px]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pb-20 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-md px-4 py-2 text-[11px] font-semibold tracking-[0.25em] uppercase text-white">
            <MapPin size={13} className="text-electric" /> Plumstead, Cape Town
          </span>
        </motion.div>

        <h1 className="font-display font-black text-white leading-[0.9] tracking-tighter text-[17vw] sm:text-[13vw] lg:text-[9.5rem]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineReveal}
                initial="hidden"
                animate="show"
                className={`block ${i === 1 ? "text-outline-light" : ""} ${i === 2 ? "text-electric" : ""}`}
                data-testid={`hero-line-${i}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-12 flex flex-col md:flex-row md:items-end justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="max-w-md text-white/80 text-base lg:text-lg leading-relaxed"
            data-testid="hero-subtext"
          >
            Patient-centred, evidence-based physiotherapy in the Southern Suburbs.
            We empower people through movement — restoring function, building confidence,
            and helping you live a healthier, fuller life.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="flex flex-wrap items-center gap-4"
          >
            <a
              href="#book"
              data-testid="hero-book-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-electric text-white text-sm font-bold tracking-[0.1em] uppercase px-8 py-4 hover:bg-white hover:text-navy transition-colors duration-300"
            >
              Book an Appointment
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <a
              href="#services"
              data-testid="hero-services-cta"
              className="inline-flex items-center gap-3 rounded-full border border-white/40 text-white text-sm font-bold tracking-[0.1em] uppercase px-8 py-4 hover:bg-white/10 transition-colors duration-300"
            >
              Our Services
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 right-8 hidden lg:flex flex-col items-center gap-3 text-white/60"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase rotate-90 origin-center translate-y-[-8px]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
};
